import http, { request } from './http';
import { useAuthStore } from '@/stores/useAuthStore';

/**
 * 权限信息
 */
export interface Permission {
  code: string;
  name: string;
  resource: string;
  action: string;
  description?: string;
}

/**
 * 角色信息
 */
export interface Role {
  id: number;
  code: string;
  name: string;
  description?: string;
}

/**
 * 用户权限响应
 */
export interface UserPermissionsResponse {
  userId: number;
  permissions: string[];
  roles: string[];
  matrixLeaves?: string[];
}

/**
 * 获取当前用户的权限列表
 * 后端接口：GET /api/permissions/me
 */
export async function getUserPermissions(): Promise<UserPermissionsResponse> {
  const raw = await request<{ permissions: string[]; roles: string[]; matrixLeaves?: string[] }>(
    http.get('/permissions/me'),
  );
  const authStore = useAuthStore();
  const userId =
    (authStore.currentUser && (authStore.currentUser as any).userId) || 0;
  return {
    userId,
    permissions: raw.permissions || [],
    roles: raw.roles || [],
    matrixLeaves: raw.matrixLeaves || [],
  };
}

/**
 * 检查用户是否拥有指定权限
 */
export async function checkPermission(permissionCode: string): Promise<boolean> {
  try {
    const response = await request<{ hasPermission: boolean }>(
      // 若需要精确用户维度的校验，可改用 /permissions/users/{userId}/check
      http.get(
        `/permissions/me?permission=${encodeURIComponent(permissionCode)}`,
      ),
    );
    return response.hasPermission;
  } catch {
    return false;
  }
}

/**
 * 检查用户是否拥有多个权限（AND逻辑）
 */
export async function checkPermissions(permissionCodes: string[]): Promise<boolean> {
  try {
    const response = await request<{ hasPermission: boolean }>(
      http.post('/permissions/check', { permissions: permissionCodes }),
    );
    return response.hasPermission;
  } catch {
    return false;
  }
}

/**
 * 检查用户是否拥有指定角色
 */
export async function checkRole(roleCode: string): Promise<boolean> {
  try {
    const response = await request<{ hasRole: boolean }>(
      http.get(`/permissions/roles/check?role=${encodeURIComponent(roleCode)}`),
    );
    return response.hasRole;
  } catch {
    return false;
  }
}

/**
 * 获取所有权限列表（用于权限管理）
 */
export async function getAllPermissions(): Promise<Permission[]> {
  return request<Permission[]>(http.get('/permissions'));
}

/**
 * 获取所有角色列表（用于权限管理）
 */
export async function getAllRoles(): Promise<Role[]> {
  return request<Role[]>(http.get('/permissions/roles'));
}
